import { Component, inject, Input, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { ButtonComponent, LoaderModule, DialogModule } from '@mds/ui';
import { SlideToggleComponent } from '@mds/ui';
import { ToastrService } from '@services/toaster.service';
import { SSOSettingsService } from './sso-provider-settings.service';

import {
  SSOConfiguration,
  SSOConfigurationUpdateRequest,
  SSOFieldSchema,
  PROVIDER_TYPE_OPTIONS,
  getRequiredFields
} from './sso-provider-settings.model';
import { ConfirmationDialogComponent } from '@dialogs/confirmation-dialog/confirmation-dialog.component';
import { MdsIcon } from '@mds/icons';

interface ConfigFormData {
  [key: string]: string | boolean | Record<string, string>;
}

@Component({
  selector: 'app-sso-provider-settings',
  standalone: true,
  imports: [
    FormsModule,
    MatDialogModule,
    MatExpansionModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatSelectModule,
    ButtonComponent,
    LoaderModule,
    DialogModule,
    SlideToggleComponent
  ],
  templateUrl: './sso-provider-settings.component.html',
  styleUrls: ['../../team-common.scss', './sso-provider-settings.component.scss']
})
export class SsoProviderSettingsComponent implements OnInit {
  @Input() teamRid: string;

  configurations: SSOConfiguration[] = [];
  isLoading = true;
  expandedPanels = new Set<number>();
  providerTypeOptions = PROVIDER_TYPE_OPTIONS;

  /** Tracks form values for each configuration by index */
  formDataMap = new Map<number, ConfigFormData>();

  /** Tracks whether a new (unsaved) configuration is being added */
  isAddingNew = false;
  newConfigFormData: ConfigFormData = {};

  private ssoSettingsService = inject(SSOSettingsService);
  private toastr = inject(ToastrService);
  private dialog = inject(MatDialog);

  ngOnInit(): void {
    this.loadConfigurations();
  }

  loadConfigurations(): void {
    this.isLoading = true;
    this.ssoSettingsService.getConfigurations(this.teamRid).subscribe({
      next: (res) => {
        this.configurations = res.configurations;
        this.buildFormDataMap();
        this.isLoading = false;
      },
      error: () => {
        this.isLoading = false;
        this.toastr.showMessage('Failed to load SSO configurations', 3000);
      }
    });
  }

  addNewConfiguration(): void {
    this.isAddingNew = true;
    this.newConfigFormData = {
      configuration_name: '',
      provider_type: '',
      provider: '',
      authorize_endpoint: '',
      exchange_token_endpoint: '',
      user_info_endpoint: '',
      redirect_uri: '',
      client_id: '',
      client_secret: '',
      grant_type: '',
      response_type: '',
      response_mode: '',
      scope: '',
      code_challenge_method: '',
      logo: '',
      ui_login_button_enabled: false,
      allow_sso_user_provision: false,
      user_attributes_mapping: { email: 'email', first_name: 'firstName', last_name: 'lastName' }
    };
  }

  cancelNewConfiguration(): void {
    this.isAddingNew = false;
    this.newConfigFormData = {};
  }

  saveNewConfiguration(): void {
    const missing = this.getMissingRequiredFields(this.newConfigFormData);
    if (missing.length > 0) {
      this.toastr.showMessage(`Missing required fields: ${missing.join(', ')}`, 4000);
      return;
    }

    const payload = this.buildPayload(this.newConfigFormData);
    this.ssoSettingsService.createConfiguration(this.teamRid, payload).subscribe({
      next: (res) => {
        this.configurations = res.configurations;
        this.buildFormDataMap();
        this.isAddingNew = false;
        this.newConfigFormData = {};
        this.toastr.showMessage('Configuration created successfully', 2000);
      },
      error: (err) => {
        this.toastr.showMessage(err?.error?.message ??'Failed to create configuration', 3000);
      }
    });
  }

  saveConfiguration(index: number): void {
    const formData = this.formDataMap.get(index);
    if (!formData) return;

    const payload = this.buildPayload(formData);
    this.ssoSettingsService.updateConfiguration(this.teamRid, payload).subscribe({
      next: (res) => {
        this.configurations = res.configurations;
        this.buildFormDataMap();
        this.toastr.showMessage('Configuration updated successfully', 2000);
      },
      error: (err) => {
        this.toastr.showMessage(err?.error?.message?? 'Failed to update configuration', 3000);
      }
    });
  }

  deleteConfiguration(config: SSOConfiguration): void {
    const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
      data: {
        type: 'Delete',
        header: 'Delete SSO Configuration?',
        text: `Configuration "${config.configuration_name}" will be permanently deleted.`,
        cancel_text: 'Cancel',
        confirm_text: 'Delete'
      }
    });

    dialogRef.afterClosed().subscribe((confirmed) => {
      if (confirmed) {
        this.ssoSettingsService.deleteConfiguration(this.teamRid, config.configuration_name).subscribe({
          next: (res) => {
            this.configurations = res.configurations;
            this.buildFormDataMap();
            this.toastr.showMessage('Configuration deleted successfully', 2000);
          },
          error: () => {
            this.toastr.showMessage('Failed to delete configuration', 3000);
          }
        });
      }
    });
  }

  togglePanel(index: number): void {
    if (this.expandedPanels.has(index)) {
      this.expandedPanels.delete(index);
    } else {
      this.expandedPanels.add(index);
    }
  }

  getFormData(index: number): ConfigFormData {
    return this.formDataMap.get(index) || {};
  }

  getFieldType(key: string): string {
    if (key === 'client_secret') return 'password';
    if (key === 'provider_type') return 'select';
    return 'text';
  }

  /** Fields that should be disabled on existing (saved) configurations */
  private static readonly LOCKED_FIELDS = new Set(['configuration_name', 'provider', 'provider_type']);

  isLockedField(key: string): boolean {
    return SsoProviderSettingsComponent.LOCKED_FIELDS.has(key);
  }

  /** Check if a field is required for the given provider_type */
  isRequired(fieldKey: string, formData: ConfigFormData): boolean {
    const providerType = (formData['provider_type'] as string) || '';
    return getRequiredFields(providerType).has(fieldKey);
  }

  isTextField(field: SSOFieldSchema): boolean {
    return field.type === 'text' && field.key !== 'provider_type';
  }

  isSelectField(field: SSOFieldSchema): boolean {
    return field.key === 'provider_type';
  }

  isCheckboxField(field: SSOFieldSchema): boolean {
    return field.type === 'checkbox';
  }

  isJsonField(field: SSOFieldSchema): boolean {
    return field.type === 'json';
  }

  getJsonString(value: string | boolean | Record<string, string>): string {
    if (typeof value === 'object' && value !== null) {
      return JSON.stringify(value, null, 2);
    }
    return typeof value === 'string' ? value : '';
  }

  onJsonFieldChange(formData: ConfigFormData, key: string, value: string): void {
    try {
      formData[key] = JSON.parse(value);
    } catch {
      // Keep the raw string until valid JSON is entered
      formData[key] = value;
    }
  }

  /** Returns a list of required field keys that are empty or missing in the form data */
  private getMissingRequiredFields(formData: ConfigFormData): string[] {
    const providerType = (formData['provider_type'] as string) || '';
    const required = getRequiredFields(providerType);
    const missing: string[] = [];

    required.forEach((key) => {
      const value = formData[key];
      if (value === undefined || value === null || value === '') {
        missing.push(key);
      }
    });

    return missing;
  }

  private buildFormDataMap(): void {
    this.formDataMap.clear();
    this.configurations.forEach((config, index) => {
      const data: ConfigFormData = {};
      config.fields.forEach((field) => {
        data[field.key] = field.value;
      });
      this.formDataMap.set(index, data);
    });
  }

  private buildPayload(formData: ConfigFormData): SSOConfigurationUpdateRequest {
    const payload: SSOConfigurationUpdateRequest = {
      configuration_name: formData['configuration_name'] as string
    };

    Object.entries(formData).forEach(([key, value]) => {
      if (key === 'configuration_name') return;
      if (value === '' || value === null || value === undefined) return;
      (payload as any)[key] = value;
    });

    return payload;
  }

  protected readonly icons = MdsIcon;
}
